/**
 * TIME TRACKING TAB - Shared Component
 *
 * Displays and allows adding/editing time entries for work items.
 * Shows total hours, billable vs non-billable breakdown.
 *
 * @version 1.2.0
 * @date April 2026
 */

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/time-tracking.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/time-tracking)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { AppointmentTimerDisplay } from '@/hooks/useAppointmentLiveTimers';
import {
  Briefcase,
  Clock,
  DollarSign,
  Lock,
  Navigation,
  Pause,
  Pencil,
  Plus,
  Square,
  Timer,
  X,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { confirm } from '@/lib/notifications';
import notify from '@/lib/notifications';
import AppointmentReferenceBadge from '@/components/work-items/AppointmentReferenceBadge';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import UserDocsLink from '@/components/shared/UserDocsLink';
import {
  formatDateSAST,
  formatDateTimeSAST,
  formatTimeSAST,
  formatLocalISOSAST,
} from '@/lib/sastTime';
import AppointmentScheduler, {
  formatLocalDateTimeValue,
  getDefaultAppointmentSchedule,
} from '@/components/work-items/AppointmentScheduler';
import {
  getGroupedTravelSessionKey,
  getTravelDistanceKmFromMetadata,
  resolveGroupedTravelSessions,
} from '@/lib/work-items/groupedTravelDistance';

interface TimeEntry {
  id: string;
  entry_date: string;
  started_at?: string;
  ended_at?: string;
  duration_hours: number;
  duration_minutes?: number;
  description?: string;
  is_billable: boolean;
  is_running?: boolean;
  activity_type?: 'travel' | 'work' | 'on_site' | 'break' | 'wait' | 'admin' | 'manual' | string;
  tracked_via?: 'manual' | 'workflow' | 'mobile_app' | 'auto' | 'desktop' | string;
  user_name?: string;
  first_name?: string;
  last_name?: string;
  user_id?: number;
  created_at: string;
  metadata?: Record<string, unknown>;
  source_item_id?: string;
  source_item_number?: string;
  source_item_type?: string;
  is_from_related?: boolean;
}

interface BillingToggleSettings {
  bill_travel_time: boolean;
  bill_travel_km: boolean;
  bill_onsite_labour: boolean;
}

interface TimeTrackingTabProps {
  workItemId: string;
  relatedWorkItemIds?: string[];
  includeRelated?: boolean;
  isActive: boolean;
  isDark?: boolean;
  readOnly?: boolean;
  billingLocked?: boolean;
  openComposerToken?: number;
  liveTimer?: AppointmentTimerDisplay | null;
  timerAggregationMode?: 'individual' | 'team';
  billingSettings?: BillingToggleSettings;
  workItemType?: string;
}

interface TimeEntryFormState {
  scheduled_start: string;
  scheduled_end: string;
  description: string;
  is_billable: boolean;
  activity_type: string;
  distance_km: string;
}

const getAuthToken = (): string => authService.getTokens()?.access_token || '';

function fmtDuration(hours: number): string {
  if (!hours || hours <= 0) return '0m';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  const totalMinutes = Math.round(hours * 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m (${totalMinutes}m)`;
}

function getTravelKm(entry: TimeEntry): number | null {
  const distanceKm = getTravelDistanceKmFromMetadata(entry.metadata);
  return distanceKm > 0 ? Math.round(distanceKm * 10) / 10 : null;
}

function parseEntryMetadata(metadata: unknown): Record<string, unknown> {
  if (!metadata) {
    return {};
  }

  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata);
      return parsed && typeof parsed === 'object'
        ? parsed as Record<string, unknown>
        : {};
    } catch {
      return {};
    }
  }

  return typeof metadata === 'object'
    ? metadata as Record<string, unknown>
    : {};
}

function getOriginalDurationHours(entry: TimeEntry): number | null {
  const metadata = parseEntryMetadata(entry.metadata);
  const originalDuration = metadata.original_duration_hours;
  return typeof originalDuration === 'number' && originalDuration > 0 ? originalDuration : null;
}

function getEntryDisplayName(entry: TimeEntry): string {
  const fullName = [entry.first_name, entry.last_name].filter(Boolean).join(' ').trim();
  return fullName || entry.user_name || 'Unknown user';
}

function getEntryDurationHoursAtTimestamp(entry: TimeEntry, nowMs: number): number {
  const durationHours = Number(entry.duration_hours);
  if (Number.isFinite(durationHours) && durationHours >= 0 && !entry.is_running) {
    return durationHours;
  }

  const durationMinutes = Number(entry.duration_minutes);
  if (Number.isFinite(durationMinutes) && durationMinutes >= 0 && !entry.is_running) {
    return durationMinutes / 60;
  }

  const startedAtMs = entry.started_at ? new Date(entry.started_at).getTime() : Number.NaN;
  const endedAtMs = entry.ended_at ? new Date(entry.ended_at).getTime() : nowMs;

  if (Number.isFinite(startedAtMs) && Number.isFinite(endedAtMs) && endedAtMs > startedAtMs) {
    return (endedAtMs - startedAtMs) / (1000 * 60 * 60);
  }

  return Number.isFinite(durationHours) && durationHours >= 0 ? durationHours : 0;
}

function mergeOverlappingIntervalsHours(entries: TimeEntry[], nowMs: number): number {
  if (entries.length === 0) return 0;
  if (entries.length === 1) return getEntryDurationHoursAtTimestamp(entries[0], nowMs);

  const withTs = entries.filter((e) => e.started_at);
  const withoutTs = entries.filter((e) => !e.started_at);

  let totalWithoutTs = 0;
  if (withoutTs.length > 0) {
    totalWithoutTs = Math.max(...withoutTs.map((e) => getEntryDurationHoursAtTimestamp(e, nowMs)));
  }

  if (withTs.length === 0) return totalWithoutTs;

  const intervals = withTs.map((e) => {
    const startMs = new Date(e.started_at!).getTime();
    let endMs: number;
    if (e.ended_at) {
      endMs = new Date(e.ended_at).getTime();
    } else if (e.is_running) {
      endMs = nowMs;
    } else {
      endMs = startMs + getEntryDurationHoursAtTimestamp(e, nowMs) * 3_600_000;
    }
    return { startMs, endMs: Math.max(endMs, startMs) };
  });

  intervals.sort((a, b) => a.startMs - b.startMs);

  const merged: { startMs: number; endMs: number }[] = [];
  for (const interval of intervals) {
    const last = merged[merged.length - 1];
    if (last && interval.startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, interval.endMs);
    } else {
      merged.push({ startMs: interval.startMs, endMs: interval.endMs });
    }
  }

  const mergedHours = merged.reduce((sum, m) => sum + (m.endMs - m.startMs) / 3_600_000, 0);
  return mergedHours + totalWithoutTs;
}

function getCurrentUserIdentity() {
  const user = authService.getUser() as (Record<string, unknown> & {
    id?: number | string | null;
    role?: string | null;
    active_client_role?: string | null;
    permissions?: { role?: string | null } | null;
    is_admin?: boolean;
  }) | null;
  const currentUserId = user?.id != null ? Number(user.id) : null;
  const role = String(
    user?.permissions?.role
    || user?.active_client_role
    || user?.role
    || ''
  ).trim().toLowerCase();

  return {
    currentUserId,
    role,
    isAdminLike: user?.is_admin === true || ['admin', 'owner', 'root'].includes(role),
    isSupervisorLike: role === 'supervisor',
  };
}

function createDefaultFormState(): TimeEntryFormState {
  const defaults = getDefaultAppointmentSchedule(new Date(), 60);
  return {
    scheduled_start: defaults.scheduled_start,
    scheduled_end: defaults.scheduled_end,
    description: '',
    is_billable: true,
    activity_type: 'work',
    distance_km: '',
  };
}

function getFormStateFromEntry(entry: TimeEntry): TimeEntryFormState {
  const startedAt = entry.started_at ? new Date(entry.started_at) : new Date(entry.created_at);
  const endedAt = entry.ended_at ? new Date(entry.ended_at) : null;

  const metadata = parseEntryMetadata(entry.metadata);
  const rawDistance = metadata.distance_km ?? metadata.travel_distance_km ?? null;
  const distanceKm = typeof rawDistance === 'number' && rawDistance > 0 ? String(rawDistance) : '';

  return {
    scheduled_start: formatLocalDateTimeValue(startedAt),
    scheduled_end: endedAt ? formatLocalDateTimeValue(endedAt) : '',
    description: entry.description || '',
    is_billable: entry.is_billable !== false,
    activity_type: entry.activity_type || 'work',
    distance_km: distanceKm,
  };
}

function calculateDurationHours(formState: TimeEntryFormState): number | null {
  if (!formState.scheduled_start || !formState.scheduled_end) {
    return null;
  }

  const startedAt = new Date(formState.scheduled_start);
  const endedAt = new Date(formState.scheduled_end);

  if (Number.isNaN(startedAt.getTime()) || Number.isNaN(endedAt.getTime())) {
    return null;
  }

  const diffMs = endedAt.getTime() - startedAt.getTime();

  if (!Number.isFinite(diffMs) || diffMs <= 0) {
    return null;
  }

  return diffMs / (1000 * 60 * 60);
}

function formatEntryDateLabel(entry: TimeEntry): string {
  return formatDateSAST(entry.started_at || entry.entry_date || entry.created_at, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function TimeTrackingTab({
  workItemId,
  relatedWorkItemIds,
  includeRelated,
  isActive,
  isDark = false,
  readOnly = false,
  billingLocked = false,
  openComposerToken,
  liveTimer,
  timerAggregationMode = 'individual',
  billingSettings,
  workItemType,
}: TimeTrackingTabProps) {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const hasLoadedRef = useRef(false);
  const [formData, setFormData] = useState<TimeEntryFormState>(createDefaultFormState);

  const { currentUserId, isAdminLike, isSupervisorLike } = useMemo(() => getCurrentUserIdentity(), []);

  const trackedWorkItemIds = useMemo(() => {
    const ids = [workItemId, ...(relatedWorkItemIds || [])]
      .map((value) => String(value || '').trim())
      .filter(Boolean);

    return Array.from(new Set(ids));
  }, [relatedWorkItemIds, workItemId]);

  const trackedWorkItemIdSet = useMemo(() => new Set(trackedWorkItemIds), [trackedWorkItemIds]);
  const isParentBillingWorkspace = useMemo(() => (relatedWorkItemIds?.length || 0) > 0, [relatedWorkItemIds]);
  const shouldIncludeRelated = includeRelated ?? isParentBillingWorkspace;

  // Reset + reload only when the primary workItemId changes. The related
  // work item IDs are used for event-dedup (trackedWorkItemIdSet) and may
  // opt the view into `include_related=true` when this is a parent billing
  // workspace.
  // Resetting on their change caused a double-fetch on first load because
  // BillingDashboard populates related IDs asynchronously after `loadOverview`
  // resolves.
  useEffect(() => {
    hasLoadedRef.current = false;
    setEntries([]);
    setLoading(true);
  }, [workItemId]);

  const resetForm = useCallback(() => {
    setFormData(createDefaultFormState());
    setEditingEntry(null);
  }, []);

  const closeForm = useCallback(() => {
    setShowForm(false);
    resetForm();
  }, [resetForm]);

  const loadTimeEntries = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (shouldIncludeRelated) {
        queryParams.set('include_related', 'true');
      }

      const queryString = queryParams.toString();
      const response = await fetch(`/api/v1/work-items/${workItemId}/time-entries${queryString ? `?${queryString}` : ''}`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setEntries(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading time entries:', error);
    } finally {
      setLoading(false);
    }
  }, [shouldIncludeRelated, workItemId]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      void loadTimeEntries();
      hasLoadedRef.current = true;
    }
  }, [isActive, loadTimeEntries]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const refreshEntries = (event?: Event) => {
      const detail = (event as CustomEvent<{ workItemId?: string; workItemIds?: string[] }>)?.detail;
      const detailWorkItemIds = Array.from(new Set([
        ...(detail?.workItemId ? [String(detail.workItemId)] : []),
        ...((detail?.workItemIds || []).map((value) => String(value || '').trim()).filter(Boolean)),
      ]));

      if (detailWorkItemIds.length > 0 && !detailWorkItemIds.some((id) => trackedWorkItemIdSet.has(id))) {
        return;
      }

      hasLoadedRef.current = true;
      void loadTimeEntries();
    };

    window.addEventListener('workItemStatusChange', refreshEntries);
    window.addEventListener('workItemTimeEntriesChanged', refreshEntries as EventListener);

    return () => {
      window.removeEventListener('workItemStatusChange', refreshEntries);
      window.removeEventListener('workItemTimeEntriesChanged', refreshEntries as EventListener);
    };
  }, [isActive, loadTimeEntries, trackedWorkItemIdSet]);

  useEffect(() => {
    if (readOnly && showForm) {
      closeForm();
    }
  }, [closeForm, readOnly, showForm]);

  useEffect(() => {
    if (!isActive || readOnly || !openComposerToken) {
      return;
    }

    resetForm();
    setShowForm(true);
  }, [isActive, openComposerToken, readOnly, resetForm]);

  const canManageEntry = useCallback((entry: TimeEntry) => {
    if (readOnly) {
      return false;
    }

    if (isAdminLike) {
      return true;
    }

    if (isSupervisorLike && isParentBillingWorkspace) {
      return true;
    }

    return currentUserId != null && Number(entry.user_id) === Number(currentUserId);
  }, [currentUserId, isAdminLike, isParentBillingWorkspace, isSupervisorLike, readOnly]);

  const [stoppingEntryId, setStoppingEntryId] = useState<string | null>(null);

  const handleStopTimer = useCallback(async (entry: TimeEntry) => {
    if (!canManageEntry(entry) || readOnly) return;

    const endedAt = formatLocalISOSAST(new Date());

    try {
      setStoppingEntryId(entry.id);
      const response = await fetch(`/api/v1/work-items/${workItemId}/time-entries/${entry.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ended_at: endedAt,
          is_running: false,
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to stop timer');
      }

      await loadTimeEntries();
      window.dispatchEvent(new CustomEvent('workItemTimeEntriesChanged', {
        detail: { workItemId, workItemIds: [workItemId] },
      }));
      notify.success(`Timer stopped for ${getEntryDisplayName(entry)}`);
    } catch (error) {
      console.error('Error stopping time entry:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to stop timer');
    } finally {
      setStoppingEntryId(null);
    }
  }, [canManageEntry, loadTimeEntries, readOnly, workItemId]);

  const computedDurationHours = useMemo(() => calculateDurationHours(formData), [formData]);
  const isEditingRunningEntry = Boolean(editingEntry?.is_running);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting || readOnly) return;

    const requiresEndTime = !isEditingRunningEntry;
    if (!formData.scheduled_start || (requiresEndTime && !formData.scheduled_end)) {
      notify.error('Please capture the start and finish date-time values before saving.');
      return;
    }

    if (!isEditingRunningEntry && !computedDurationHours) {
      notify.error('Finish time must be later than start time.');
      return;
    }

    // Check if this is a manual timer add or auto timer edit
    const isManualTimerAdd = !editingEntry;
    const isAutoTimerEdit = editingEntry && editingEntry.tracked_via !== 'manual';

    let overrideReason = '';

    // Require confirmation with reason for manual timer adds and auto timer edits
    if (isManualTimerAdd || isAutoTimerEdit) {
      const confirmationMessage = isManualTimerAdd
        ? 'You are adding a manual time entry. Please provide a reason for this manual entry.'
        : 'You are editing an auto-tracked timer. Please provide a reason for this edit.';

      const confirmed = await confirm({
        title: isManualTimerAdd ? 'Add Manual Timer' : 'Edit Auto Timer',
        message: confirmationMessage,
        confirmText: isManualTimerAdd ? 'Add Timer' : 'Save Changes',
        cancelText: 'Cancel',
        variant: 'warning',
        requireConfirmation: {
          type: 'textarea',
          label: 'Reason for this action',
          placeholder: 'Explain why this manual entry or edit is necessary...',
          minLength: 12,
        },
        onConfirm: async (result) => {
          console.log('Confirmation result:', result);
          if (result?.inputValue) {
            overrideReason = result.inputValue;
          }
        },
      });

      if (!confirmed) {
        return;
      }
    }

    const startedAt = formatLocalISOSAST(new Date(formData.scheduled_start));
    const endedAt = formData.scheduled_end ? formatLocalISOSAST(new Date(formData.scheduled_end)) : null;
    const url = editingEntry
      ? `/api/v1/work-items/${workItemId}/time-entries/${editingEntry.id}`
      : `/api/v1/work-items/${workItemId}/time-entries`;
    const method = editingEntry ? 'PUT' : 'POST';

    const payload: Record<string, unknown> = {
      started_at: startedAt,
      description: formData.description,
      is_billable: formData.is_billable,
      ended_at: endedAt,
    };

    // Add override reason to metadata if provided
    if (overrideReason) {
      payload.metadata = {
        override_reason: overrideReason,
      };
    }

    // Store original duration when editing an auto-tracked timer
    if (editingEntry && editingEntry.tracked_via !== 'manual') {
      const originalDurationHours = getEntryDurationHoursAtTimestamp(editingEntry, Date.now());
      if (originalDurationHours > 0) {
        payload.metadata = {
          ...(payload.metadata as Record<string, unknown> || {}),
          original_duration_hours: originalDurationHours,
        };
      }
    }

    if (isEditingRunningEntry && formData.scheduled_end) {
      payload.is_running = false;
    }

    payload.activity_type = formData.activity_type;
    if (!editingEntry) {
      payload.tracked_via = 'manual';
    }
    if (formData.activity_type === 'travel') {
      const distanceKm = parseFloat(formData.distance_km);
      if (Number.isFinite(distanceKm) && distanceKm > 0) {
        if (editingEntry) {
          payload.metadata = {
            ...(payload.metadata as Record<string, unknown> || {}),
            distance_km: distanceKm,
          };
        } else {
          payload.travel_distance_km = distanceKm;
        }
      }
    }

    try {
      setSubmitting(true);
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to save time entry');
      }

      await loadTimeEntries();
      window.dispatchEvent(new CustomEvent('workItemTimeEntriesChanged', {
        detail: { workItemId, workItemIds: [workItemId] },
      }));
      notify.success(editingEntry ? 'Time entry updated' : 'Time entry added');
      closeForm();
    } catch (error) {
      console.error('Error saving time entry:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to save time entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = useCallback((entry: TimeEntry) => {
    if (!canManageEntry(entry)) {
      notify.warning('Only the entry owner or a permitted manager can edit this timer from the current workspace.');
      return;
    }

    setEditingEntry(entry);
    setFormData(getFormStateFromEntry(entry));
    setShowForm(true);
  }, [canManageEntry]);

  const handleDelete = useCallback(async (entry: TimeEntry) => {
    if (!canManageEntry(entry)) {
      notify.warning('Only the entry owner or a permitted manager can delete this timer from the current workspace.');
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Time Entry',
      message: 'Delete this time entry? This action is audited and cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/work-items/${workItemId}/time-entries/${entry.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to delete time entry');
      }

      await loadTimeEntries();
      window.dispatchEvent(new CustomEvent('workItemTimeEntriesChanged', {
        detail: { workItemId, workItemIds: [workItemId] },
      }));
      notify.success('Time entry deleted');
    } catch (error) {
      console.error('Error deleting time entry:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to delete time entry');
    }
  }, [canManageEntry, loadTimeEntries, workItemId]);

  const [now, setNow] = useState(Date.now());

  const liveTimerSummary = useMemo(() => {
    if (!liveTimer) {
      return null;
    }

    const activityLabel = liveTimer.activeState === 'en_route' ? 'Travel' : 'On-site';
    const statusLabel = liveTimer.isPaused ? 'paused' : 'running';

    return {
      label: `${activityLabel} timer ${statusLabel}`,
      value: liveTimer.elapsedFormatted,
      detail: `${activityLabel} timer ${statusLabel}${liveTimer.userName ? ` for ${liveTimer.userName}` : ''}`,
      active: !liveTimer.isPaused,
    };
  }, [liveTimer]);

  // Filter entries based on billing toggle settings
  const billingFilteredEntries = useMemo(() => {
    if (!billingSettings) return entries;
    return entries.filter((entry) => {
      if (entry.activity_type === 'travel' && !billingSettings.bill_travel_time) {
        return false;
      }
      if ((entry.activity_type === 'work' || entry.activity_type === 'on_site') && !billingSettings.bill_onsite_labour) {
        return false;
      }
      return true;
    });
  }, [entries, billingSettings]);

  // Group entries by user for per-attendee display
  const entriesByUser = useMemo(() => {
    const grouped = new Map<number | string, TimeEntry[]>();
    billingFilteredEntries.forEach((entry) => {
      const userId = entry.user_id || 'unknown';
      if (!grouped.has(userId)) {
        grouped.set(userId, []);
      }
      grouped.get(userId)!.push(entry);
    });
    return grouped;
  }, [billingFilteredEntries]);

  // Group entries by date for chronological display
  const entriesByDate = useMemo(() => {
    const grouped = new Map<string, TimeEntry[]>();
    billingFilteredEntries.forEach((entry) => {
      const rawDate = entry.started_at || entry.entry_date || entry.created_at;
      const dateObj = new Date(rawDate);
      const dateKey = dateObj.toISOString().slice(0, 10);
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(entry);
    });
    const sorted = new Map<string, TimeEntry[]>(
      Array.from(grouped.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, items]) => [key, items.slice().sort((a, b) => {
          const aTime = new Date(a.started_at || a.entry_date || a.created_at).getTime();
          const bTime = new Date(b.started_at || b.entry_date || b.created_at).getTime();
          return aTime - bTime;
        })])
    );
    return sorted;
  }, [billingFilteredEntries]);

  // Group entries by appointment for team-mode display
  const entriesByAppointment = useMemo(() => {
    const grouped = new Map<string, TimeEntry[]>();
    billingFilteredEntries.forEach((entry) => {
      const apptKey = entry.source_item_id || entry.source_item_number || 'direct';
      if (!grouped.has(apptKey)) {
        grouped.set(apptKey, []);
      }
      grouped.get(apptKey)!.push(entry);
    });
    return grouped;
  }, [billingFilteredEntries]);

  const isTeamMode = timerAggregationMode === 'team';

  // When only one user is present, team mode should produce identical results to individual mode
  const hasSingleUser = useMemo(() => {
    if (billingFilteredEntries.length <= 1) return true;
    const userIds = new Set<string | number>();
    const displayNames = new Set<string>();
    for (const entry of billingFilteredEntries) {
      if (entry.user_id != null) userIds.add(entry.user_id);
      displayNames.add(getEntryDisplayName(entry));
    }
    // Single user if all entries share the same user_id (when available) or same display name
    if (userIds.size > 0) return userIds.size === 1;
    return displayNames.size === 1;
  }, [billingFilteredEntries]);

  // Team aggregation only applies when there are multiple users
  const effectiveTeamMode = isTeamMode && !hasSingleUser;

  // In team mode, aggregate entries by appointment+date+activity_type (merged non-overlapping duration, combined names)
  const teamAggregatedEntries = useMemo(() => {
    if (!effectiveTeamMode) return billingFilteredEntries;

    const buckets = new Map<string, {
      primary: TimeEntry;
      allEntries: TimeEntry[];
      userNames: Set<string>;
    }>();

    billingFilteredEntries.forEach((entry) => {
      const rawDate = entry.started_at || entry.entry_date || entry.created_at;
      const dateKey = rawDate ? new Date(rawDate).toISOString().slice(0, 10) : 'no-date';
      const apptKey = entry.source_item_id || entry.source_item_number || 'direct';
      const activityKey = entry.activity_type || 'default';
      const bucketKey = `${apptKey}:${dateKey}:${activityKey}`;

      const existing = buckets.get(bucketKey);
      if (!existing) {
        buckets.set(bucketKey, {
          primary: entry,
          allEntries: [entry],
          userNames: new Set([getEntryDisplayName(entry)]),
        });
      } else {
        existing.allEntries.push(entry);
        existing.userNames.add(getEntryDisplayName(entry));
        const existingStart = existing.primary.started_at ? new Date(existing.primary.started_at).getTime() : Infinity;
        const entryStart = entry.started_at ? new Date(entry.started_at).getTime() : Infinity;
        if (entryStart < existingStart) {
          existing.primary = entry;
        }
      }
    });

    // Build virtual entries from buckets
    return Array.from(buckets.values()).map((bucket) => {
      const names = Array.from(bucket.userNames);
      const primary = bucket.primary;
      const mergedDuration = mergeOverlappingIntervalsHours(bucket.allEntries, now);
      return {
        ...primary,
        id: `team:${primary.source_item_id || 'direct'}:${primary.started_at || primary.entry_date}:${primary.activity_type}`,
        user_name: names.join(', '),
        first_name: names.length > 1 ? 'Team' : primary.first_name,
        last_name: names.length > 1 ? `(${names.length})` : primary.last_name,
        duration_hours: mergedDuration,
        duration_minutes: Math.round(mergedDuration * 60),
        description: names.length > 1
          ? `Team (${names.join(', ')}) — ${primary.description || ''}`.trim()
          : primary.description,
        is_running: bucket.allEntries.some((e) => e.is_running),
      } as TimeEntry;
    });
  }, [billingFilteredEntries, effectiveTeamMode, now]);

  // Group aggregated team entries by date for display
  const teamEntriesByDate = useMemo(() => {
    if (!effectiveTeamMode) return new Map<string, TimeEntry[]>();
    const grouped = new Map<string, TimeEntry[]>();
    teamAggregatedEntries.forEach((entry) => {
      const rawDate = entry.started_at || entry.entry_date || entry.created_at;
      const dateObj = new Date(rawDate);
      const dateKey = dateObj.toISOString().slice(0, 10);
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(entry);
    });
    const sorted = new Map<string, TimeEntry[]>(
      Array.from(grouped.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, items]) => [key, items.slice().sort((a, b) => {
          const aTime = new Date(a.started_at || a.entry_date || a.created_at).getTime();
          const bTime = new Date(b.started_at || b.entry_date || b.created_at).getTime();
          return aTime - bTime;
        })])
    );
    return sorted;
  }, [teamAggregatedEntries, effectiveTeamMode]);

  // In team mode, totals use merged non-overlapping duration per appointment+date+activity_type
  const totalHours = useMemo(() => {
    if (!effectiveTeamMode) {
      return billingFilteredEntries.reduce((sum, entry) => sum + getEntryDurationHoursAtTimestamp(entry, now), 0);
    }
    const buckets = new Map<string, TimeEntry[]>();
    billingFilteredEntries.forEach((entry) => {
      const rawDate = entry.started_at || entry.entry_date || entry.created_at;
      const dateKey = rawDate ? new Date(rawDate).toISOString().slice(0, 10) : 'no-date';
      const key = `${entry.source_item_id || entry.source_item_number || 'direct'}:${dateKey}:${entry.activity_type || 'default'}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(entry);
    });
    return Array.from(buckets.values()).reduce((sum, entries) => sum + mergeOverlappingIntervalsHours(entries, now), 0);
  }, [billingFilteredEntries, now, effectiveTeamMode]);

  const billableHours = useMemo(() => {
    if (!effectiveTeamMode) {
      return billingFilteredEntries.filter((entry) => entry.is_billable).reduce((sum, entry) => sum + getEntryDurationHoursAtTimestamp(entry, now), 0);
    }
    const buckets = new Map<string, TimeEntry[]>();
    billingFilteredEntries.filter((entry) => entry.is_billable).forEach((entry) => {
      const rawDate = entry.started_at || entry.entry_date || entry.created_at;
      const dateKey = rawDate ? new Date(rawDate).toISOString().slice(0, 10) : 'no-date';
      const key = `${entry.source_item_id || entry.source_item_number || 'direct'}:${dateKey}:${entry.activity_type || 'default'}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(entry);
    });
    return Array.from(buckets.values()).reduce((sum, entries) => sum + mergeOverlappingIntervalsHours(entries, now), 0);
  }, [billingFilteredEntries, now, effectiveTeamMode]);

  const travelHours = useMemo(() => {
    if (!effectiveTeamMode) {
      return billingFilteredEntries.filter((entry) => entry.activity_type === 'travel').reduce((sum, entry) => sum + getEntryDurationHoursAtTimestamp(entry, now), 0);
    }
    const buckets = new Map<string, TimeEntry[]>();
    billingFilteredEntries.filter((entry) => entry.activity_type === 'travel').forEach((entry) => {
      const rawDate = entry.started_at || entry.entry_date || entry.created_at;
      const dateKey = rawDate ? new Date(rawDate).toISOString().slice(0, 10) : 'no-date';
      const key = `${entry.source_item_id || entry.source_item_number || 'direct'}:${dateKey}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(entry);
    });
    return Array.from(buckets.values()).reduce((sum, entries) => sum + mergeOverlappingIntervalsHours(entries, now), 0);
  }, [billingFilteredEntries, now, effectiveTeamMode]);

  const workHours = useMemo(() => {
    if (!effectiveTeamMode) {
      return billingFilteredEntries
        .filter((entry) => entry.activity_type === 'work' || entry.activity_type === 'on_site')
        .reduce((sum, entry) => sum + getEntryDurationHoursAtTimestamp(entry, now), 0);
    }
    const buckets = new Map<string, TimeEntry[]>();
    billingFilteredEntries
      .filter((entry) => entry.activity_type === 'work' || entry.activity_type === 'on_site')
      .forEach((entry) => {
        const rawDate = entry.started_at || entry.entry_date || entry.created_at;
        const dateKey = rawDate ? new Date(rawDate).toISOString().slice(0, 10) : 'no-date';
        const key = `${entry.source_item_id || entry.source_item_number || 'direct'}:${dateKey}`;
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key)!.push(entry);
      });
    return Array.from(buckets.values()).reduce((sum, entries) => sum + mergeOverlappingIntervalsHours(entries, now), 0);
  }, [billingFilteredEntries, now, effectiveTeamMode]);

  const totalTravelKm = useMemo(() => {
    if (billingSettings && !billingSettings.bill_travel_km) {
      return 0;
    }
    const travelEntries = billingFilteredEntries.filter((entry) => entry.activity_type === 'travel');
    if (travelEntries.length === 0) {
      return 0;
    }

    const groupedSessions = resolveGroupedTravelSessions(travelEntries);
    const groupedSessionKeys = new Set(groupedSessions.keys());
    let totalKm = 0;

    for (const session of groupedSessions.values()) {
      totalKm += session.distanceKm || 0;
    }

    for (const entry of travelEntries) {
      const sessionKey = getGroupedTravelSessionKey(entry);
      if (sessionKey && groupedSessionKeys.has(sessionKey)) {
        continue;
      }

      totalKm += getTravelKm(entry) || 0;
    }

    return Math.round(totalKm * 10) / 10;
  }, [billingFilteredEntries, billingSettings]);

  const runningEntries = useMemo(() => billingFilteredEntries.filter((entry) => entry.is_running), [billingFilteredEntries]);
  const hasRunningTimers = runningEntries.length > 0;
  const averageEntryHours = billingFilteredEntries.length > 0 ? totalHours / billingFilteredEntries.length : 0;
  const timeSummaryCards = [
    {
      label: 'Total captured',
      value: fmtDuration(totalHours),
      detail: effectiveTeamMode
        ? `${entriesByAppointment.size} appointment${entriesByAppointment.size === 1 ? '' : 's'} · team aggregated`
        : `${billingFilteredEntries.length} register entr${billingFilteredEntries.length === 1 ? 'y' : 'ies'}`,
      icon: Clock,
      tone: isDark ? 'border-slate-800 bg-slate-950/60' : 'border-slate-200 bg-slate-50/80',
      iconTone: isDark ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-700 border border-slate-200',
      iconAccent: 'text-slate-500',
    },
    {
      label: 'Travel time',
      value: fmtDuration(travelHours),
      detail: totalTravelKm > 0 ? `${totalTravelKm.toFixed(1)} km linked` : 'Distance captured when available',
      icon: Navigation,
      tone: isDark ? 'border-violet-900/50 bg-violet-950/20' : 'border-violet-200 bg-violet-50/80',
      iconTone: isDark ? 'bg-violet-950/60 text-violet-200' : 'bg-white text-violet-700 border border-violet-200',
      iconAccent: 'text-violet-500',
    },
    {
      label: 'On-site labour',
      value: fmtDuration(workHours),
      detail: averageEntryHours > 0 ? `Avg ${fmtDuration(averageEntryHours)} per entry` : 'Awaiting first timer',
      icon: Briefcase,
      tone: isDark ? 'border-blue-900/50 bg-blue-950/20' : 'border-blue-200 bg-blue-50/80',
      iconTone: isDark ? 'bg-blue-950/60 text-blue-200' : 'bg-white text-blue-700 border border-blue-200',
      iconAccent: 'text-blue-500',
    },
    {
      label: 'Billable ready',
      value: fmtDuration(billableHours),
      detail: effectiveTeamMode
        ? 'Team billing · max duration per appointment'
        : readOnly ? 'Finance review mode' : 'Coordinator adjustments enabled',
      icon: DollarSign,
      tone: isDark ? 'border-emerald-900/50 bg-emerald-950/20' : 'border-emerald-200 bg-emerald-50/80',
      iconTone: isDark ? 'bg-emerald-950/60 text-emerald-200' : 'bg-white text-emerald-700 border border-emerald-200',
      iconAccent: 'text-emerald-500',
    },
  ] as const;

  useEffect(() => {
    if (!hasRunningTimers) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [hasRunningTimers]);

  const formatElapsed = useCallback((startedAt: string) => {
    const elapsed = Math.floor((now - new Date(startedAt).getTime()) / 1000);
    const h = Math.floor(elapsed / 3600);
    const m = Math.floor((elapsed % 3600) / 60);
    const s = elapsed % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }, [now]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-tour-id="work-item-time-tracking-tab">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className={`rounded-full px-3 py-1.5 text-[11px] font-medium ${readOnly ? (isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600 border border-gray-200') : (isDark ? 'bg-blue-950/30 text-blue-300' : 'bg-blue-50 text-blue-700 border border-blue-200')}`}>
            {readOnly ? 'View mode only' : 'Coordinator editing enabled'}
          </span>
          {billingLocked && (
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium ${isDark ? 'bg-amber-950/40 text-amber-300 border border-amber-800/50' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
              <Lock size={12} />
              Billing locked
            </span>
          )}
          {!showForm && !readOnly && workItemType !== 'job' && (
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-blue-700"
            >
              <Plus size={16} />
              Add Time Entry
            </button>
          )}
          {workItemType === 'job' && !readOnly && (
            <span className={`text-[11px] italic ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Timers are inherited from appointments — add manual timers to individual appointments.
            </span>
          )}
          <UserDocsLink docsPath="/total-operations/workflows/service-operations/time-tracking" label="User guide: Time tracking" />
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4" data-tour-id="time-tracking-summary-cards">
          {timeSummaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className={`rounded-xl border px-4 py-3 ${card.tone}`}>
                <div className="flex items-center gap-2">
                  <div className={`rounded-lg p-2 ${card.iconTone}`}>
                    <Icon className={`h-4 w-4 ${card.iconAccent}`} />
                  </div>
                  <span className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {card.label}
                  </span>
                </div>
                <p className={`mt-2 text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{card.value}</p>
                <p className={`mt-1 text-xs leading-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{card.detail}</p>
              </div>
            );
          })}
        </div>

        {liveTimerSummary && (
          <div className={`rounded-xl border px-4 py-3 text-sm ${liveTimerSummary.active ? (isDark ? 'border-green-700 bg-green-950/30 text-green-200' : 'border-green-200 bg-green-50 text-green-800') : (isDark ? 'border-amber-700 bg-amber-950/20 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800')}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold">Live timer</span>
              <span className="font-mono">{liveTimerSummary.value}</span>
            </div>
            <p className="mt-2 text-xs leading-5 text-current">{liveTimerSummary.detail}</p>
          </div>
        )}

        <div className={`rounded-xl border px-4 py-2 text-[11px] ${isDark ? 'border-gray-800 bg-gray-950/60 text-gray-400' : 'border-gray-200 bg-slate-50 text-slate-600'}`}>
          All timestamps are displayed in SAST, duration is calculated from the editable start and end times, and the register stays ready for finance review.
        </div>
      </div>

      {hasRunningTimers && (
        <div className={`rounded-lg p-3 border-l-4 border-green-500 ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
          <div className="flex items-start gap-2">
            <div className="relative mt-0.5 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
            </div>
            <div className="flex-1">
              <div className={`text-sm font-medium ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                {runningEntries.length} active timer{runningEntries.length > 1 ? 's' : ''} running
              </div>
              <div className="mt-1 flex flex-wrap gap-2">
                {runningEntries.map((entry) => (
                  <div key={entry.id} className={`inline-flex items-center gap-2 rounded-full pl-2 pr-1 py-1 text-[11px] font-medium ${isDark ? 'bg-green-950/50 text-green-200' : 'bg-white text-green-700 border border-green-200'}`}>
                    <span>{getEntryDisplayName(entry)}</span>
                    <span className="font-mono">{entry.activity_type === 'travel' ? 'Travel' : 'Work'} · {entry.started_at ? formatElapsed(entry.started_at) : '--'}</span>
                    {canManageEntry(entry) && !readOnly && (
                      <button
                        type="button"
                        onClick={() => { void handleStopTimer(entry); }}
                        disabled={stoppingEntryId === entry.id}
                        className={`ml-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors ${isDark ? 'bg-red-900/60 text-red-200 hover:bg-red-800/80' : 'bg-red-100 text-red-700 hover:bg-red-200'} disabled:opacity-50`}
                        title="Stop this timer now"
                      >
                        <Square size={8} />
                        {stoppingEntryId === entry.id ? 'Stopping…' : 'Stop'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className={`rounded-2xl border p-4 shadow-sm ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'} space-y-3`}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                {editingEntry ? 'Edit Time Entry' : 'Add Time Entry'}
              </h4>
              <p className={`mt-1 text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Start and end times are stored and audited in SAST. Duration is computed automatically.
              </p>
            </div>
            <button
              type="button"
              onClick={closeForm}
              className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
            >
              <X size={16} />
            </button>
          </div>

          <AppointmentScheduler
            scheduledStart={formData.scheduled_start}
            scheduledEnd={formData.scheduled_end}
            onChange={(next) => setFormData((prev) => ({ ...prev, scheduled_start: next.scheduled_start, scheduled_end: next.scheduled_end }))}
            isDark={isDark}
            defaultDurationMinutes={60}
          />

          {isEditingRunningEntry && (
            <div className={`rounded-lg border-l-4 border-amber-500 px-3 py-2.5 ${isDark ? 'bg-amber-900/20 border border-amber-800/50' : 'bg-amber-50 border border-amber-200'}`}>
              <p className={`text-xs font-semibold ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                This timer is currently running
              </p>
              <p className={`mt-0.5 text-[11px] ${isDark ? 'text-amber-400/80' : 'text-amber-600'}`}>
                Set the finish date-time to stop the timer. You can also adjust the start. Saving with a finish time will stop the running timer and finalize the entry.
              </p>
            </div>
          )}

          <div className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-900/70' : 'border-gray-200 bg-white'}`}>
            <div className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Calculated duration</div>
            <div className={`mt-1 text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>
              {computedDurationHours ? fmtDuration(computedDurationHours) : isEditingRunningEntry ? 'Running timer — set the finish date-time above to finalize the duration.' : 'Select a valid start and finish date-time.'}
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className={`block text-[11px] font-semibold uppercase tracking-[0.16em] mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Activity type</label>
              {/* Manually added timers are work records — admins add them to
                  correct errors or capture work the technician could not run
                  live, never to log idle presence. On-site Presence only
                  appears when editing an entry that already is one. */}
              <div className={`grid ${editingEntry ? 'grid-cols-3' : 'grid-cols-2'} gap-1 p-1 rounded-lg border ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-100'}`}>
                <button
                  type="button"
                  onClick={() => setFormData((current) => ({ ...current, activity_type: 'work', distance_km: '' }))}
                  className={`flex items-center justify-center gap-1.5 rounded-md py-2 text-xs font-semibold transition-colors ${formData.activity_type === 'work'
                    ? (isDark ? 'bg-gray-700 text-blue-300 shadow-sm' : 'bg-white text-blue-700 shadow-sm')
                    : (isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700')
                  }`}
                >
                  <Briefcase size={14} />
                  Work
                </button>
                {editingEntry && (
                <button
                  type="button"
                  onClick={() => setFormData((current) => ({ ...current, activity_type: 'on_site', distance_km: '' }))}
                  className={`flex items-center justify-center gap-1.5 rounded-md py-2 text-xs font-semibold transition-colors ${formData.activity_type === 'on_site'
                    ? (isDark ? 'bg-gray-700 text-amber-300 shadow-sm' : 'bg-white text-amber-700 shadow-sm')
                    : (isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700')
                  }`}
                >
                  <Clock size={14} />
                  On-site Presence
                </button>
                )}
                <button
                  type="button"
                  onClick={() => setFormData((current) => ({ ...current, activity_type: 'travel' }))}
                  className={`flex items-center justify-center gap-1.5 rounded-md py-2 text-xs font-semibold transition-colors ${formData.activity_type === 'travel'
                    ? (isDark ? 'bg-gray-700 text-purple-300 shadow-sm' : 'bg-white text-purple-700 shadow-sm')
                    : (isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700')
                  }`}
                >
                  <Navigation size={14} />
                  Travel
                </button>
                {editingEntry && !['work', 'on_site', 'travel'].includes(formData.activity_type) && (
                  <button
                    type="button"
                    className={`col-span-3 flex items-center justify-center gap-1.5 rounded-md py-2 text-xs font-semibold transition-colors ${isDark ? 'bg-gray-700 text-gray-200 shadow-sm' : 'bg-white text-gray-700 shadow-sm'}`}
                  >
                    {formData.activity_type || 'manual'} (unchanged)
                  </button>
                )}
              </div>
            </div>
            {formData.activity_type === 'travel' && (
              <div>
                <label className={`block text-[11px] font-semibold uppercase tracking-[0.16em] mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Distance (km)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.distance_km}
                  onChange={(event) => setFormData((current) => ({ ...current, distance_km: event.target.value }))}
                  placeholder="e.g. 12.5"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                />
              </div>
            )}
          </div>

          <div>
            <label className={`block text-[11px] font-semibold uppercase tracking-[0.16em] mb-1.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Description</label>
            <textarea
              value={formData.description}
              onChange={(event) => setFormData((current) => ({ ...current, description: event.target.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 resize-none text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
              rows={2}
              placeholder="What did you work on?"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_billable"
              checked={formData.is_billable}
              onChange={(event) => setFormData((current) => ({ ...current, is_billable: event.target.checked }))}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="is_billable" className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <DollarSign size={14} className="inline mr-1" />
              Billable
            </label>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={closeForm}
              className={`flex-1 px-4 py-2 border rounded-lg text-sm font-medium ${isDark ? 'border-gray-600 hover:bg-gray-700 text-gray-200' : 'border-gray-300 hover:bg-gray-50 text-gray-700'}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`flex-1 px-4 py-2 text-white rounded-lg text-sm font-medium disabled:opacity-50 ${isEditingRunningEntry && formData.scheduled_end ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {submitting ? 'Saving…' : isEditingRunningEntry && formData.scheduled_end ? 'Stop Timer & Save' : editingEntry ? 'Save changes' : 'Add entry'}
            </button>
          </div>
        </form>
      )}

      {billingFilteredEntries.length === 0 ? (
        <div className={`rounded-2xl border-2 border-dashed py-10 text-center ${isDark ? 'border-gray-700 bg-gray-900/40 text-gray-400' : 'border-gray-200 bg-slate-50 text-gray-500'}`}>
          <Clock className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className="text-sm font-medium">No time entries yet</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Capture the first timer to start the audit trail for travel, labour, and billable hours.
          </p>
        </div>
      ) : (
        <div className={`overflow-hidden rounded-2xl border shadow-sm ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`} data-tour-id="time-tracking-entries-list">
          <div className={`flex items-center justify-between border-b px-4 py-2.5 ${isDark ? 'border-gray-800 bg-gray-950/50' : 'border-gray-200 bg-slate-50/80'}`}>
            <div>
              <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Register detail</p>
              <p className={`mt-0.5 text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Entry-by-entry time audit</p>
            </div>
            <div className={`rounded-full px-3 py-1 text-[11px] font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600 border border-gray-200'}`}>
              {fmtDuration(billableHours)} billable
            </div>
          </div>
          <table className={`min-w-full divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
            <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
              <tr>
                <th className={`px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                <th className={`px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Type</th>
                <th className={`px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Time</th>
                <th className={`px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Duration</th>
                <th className={`px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Description</th>
                <th className={`px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Source</th>
                <th className={`px-4 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.16em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}></th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'bg-gray-900 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              {Array.from((effectiveTeamMode ? teamEntriesByDate : entriesByDate).entries()).map(([groupKey, groupEntries]) => (
                <React.Fragment key={groupKey}>
                  {/* Group header row */}
                  <tr className={isDark ? 'bg-gray-800/50' : 'bg-slate-100/50'}>
                    <td colSpan={7} className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {formatEntryDateLabel(groupEntries[0])}
                      <span className={`ml-2 text-[10px] font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        ({groupEntries.length} entr{groupEntries.length === 1 ? 'y' : 'ies'})
                        {!effectiveTeamMode && (() => {
                          const users = new Set(groupEntries.map((e) => getEntryDisplayName(e)));
                          return users.size > 1
                            ? ` · ${Array.from(users).join(', ')}`
                            : ` · ${Array.from(users)[0]}`;
                        })()}
                        {effectiveTeamMode && (() => {
                          const users = new Set<string>();
                          groupEntries.forEach((e) => {
                            (e.user_name || '').split(', ').forEach((n) => users.add(n));
                          });
                          return users.size > 1 ? ` · team (${users.size})` : '';
                        })()}
                      </span>
                      <span className={`float-right ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                        Day total: {fmtDuration(groupEntries.reduce((sum, e) => sum + getEntryDurationHoursAtTimestamp(e, now), 0))}
                      </span>
                    </td>
                  </tr>
                  {/* Group entries */}
                  {groupEntries.map((entry) => (
                    <tr key={entry.id} className={`${isDark ? 'hover:bg-gray-800/80' : 'hover:bg-slate-50'} ${entry.is_running ? (isDark ? 'bg-green-900/10' : 'bg-green-50/50') : ''}`}>
                      <td className={`px-4 py-3 text-[13px] ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        <div className="font-medium">{formatEntryDateLabel(entry)}</div>
                        <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {getEntryDisplayName(entry)}
                        </div>
                      </td>
                  <td className="px-3 py-3 text-[13px]">
                    {entry.activity_type === 'travel' ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-full ${isDark ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                        <Navigation size={10} /> Travel
                      </span>
                    ) : entry.activity_type === 'work' || entry.activity_type === 'on_site' ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-full ${isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                        <Briefcase size={10} /> On-site Work
                      </span>
                    ) : entry.activity_type === 'break' ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-full ${isDark ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-700'}`}>
                        <Pause size={10} /> Break
                      </span>
                    ) : (
                      <span className={`px-2 py-1 text-[11px] font-medium rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        {entry.activity_type || 'Manual'}
                      </span>
                    )}
                    {entry.source_item_number && (
                      entry.source_item_type === 'appointment' ? (
                        <AppointmentReferenceBadge
                          appointmentId={entry.source_item_id}
                          appointmentNumber={entry.source_item_number}
                          className={`ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded ${isDark ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'} transition-colors`}
                          iconClassName="w-3 h-3"
                          title={`Open appointment ${entry.source_item_number}`}
                        >
                          <>
                            <span>📋 {entry.source_item_number}</span>
                          </>
                        </AppointmentReferenceBadge>
                      ) : (
                        <span className={`ml-1 inline-flex items-center px-1.5 py-0.5 text-[10px] rounded ${isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                          from {entry.source_item_number}
                        </span>
                      )
                    )}
                  </td>
                  <td className={`px-3 py-3 text-[13px] ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {entry.started_at ? (
                      <div className="space-y-0.5">
                        <div className="font-mono text-xs font-medium">
                          {formatTimeSAST(entry.started_at)}
                          {entry.ended_at ? ` — ${formatTimeSAST(entry.ended_at)}` : entry.is_running ? <span className="text-green-500 ml-1">→ now</span> : ''}
                        </div>
                        <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {formatDateTimeSAST(entry.started_at, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          {entry.ended_at ? ` • ${formatDateTimeSAST(entry.ended_at, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
                        </div>
                      </div>
                    ) : (
                      <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>—</span>
                    )}
                    {entry.activity_type === 'travel' && (!billingSettings || billingSettings.bill_travel_km) && (() => {
                      const km = getTravelKm(entry);
                      return km != null ? <div className={`text-[10px] mt-0.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>🚗 {km} km</div> : null;
                    })()}
                  </td>
                  <td className={`px-3 py-3 text-[13px] font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {entry.is_running ? (
                      <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 font-mono">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        {entry.started_at ? formatElapsed(entry.started_at) : '--:--:--'}
                      </span>
                    ) : (
                      <div>
                        <span>{fmtDuration(parseFloat(String(entry.duration_hours || 0)))}</span>
                        {(() => {
                          const originalDuration = getOriginalDurationHours(entry);
                          return originalDuration != null ? (
                            <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              Original: {fmtDuration(originalDuration)}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </td>
                  <td className={`px-3 py-3 text-[13px] max-w-[240px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <div className="truncate">{entry.description || '—'}</div>
                  </td>
                  <td className="px-3 py-3 text-[13px]">
                    {entry.tracked_via === 'workflow' ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-full ${isDark ? 'bg-cyan-900/50 text-cyan-300' : 'bg-cyan-100 text-cyan-700'}`}>
                        <Timer size={10} /> Auto
                      </span>
                    ) : (
                      <span className={`px-2 py-1 text-[11px] font-medium rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        Manual
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {canManageEntry(entry) && !String(entry.id).startsWith('team:') && (
                      <div className="flex items-center justify-center gap-1.5">
                        {entry.is_running && !readOnly && (
                          <button
                            onClick={() => { void handleStopTimer(entry); }}
                            disabled={stoppingEntryId === entry.id}
                            className={`inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-medium transition-colors ${isDark ? 'bg-red-900/40 text-red-300 hover:bg-red-800/60' : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'} disabled:opacity-50`}
                            title="Stop this running timer"
                          >
                            <Square size={10} />
                            {stoppingEntryId === entry.id ? 'Stopping…' : 'Stop'}
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(entry)}
                          className={`p-1.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 ${isDark ? 'text-gray-400 hover:text-blue-300' : 'text-gray-500 hover:text-blue-600'}`}
                          title={entry.is_running ? 'Edit timer — adjust start/end time' : 'Edit entry'}
                        >
                          <Pencil size={14} />
                        </button>
                        {!entry.is_running && (
                          <button
                            onClick={() => void handleDelete(entry)}
                            className={`p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 ${isDark ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-600'}`}
                            title="Delete entry"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                  ))}
                </React.Fragment>
              ))}
              {/* Grand total row */}
              <tr className={isDark ? 'bg-gray-800 border-t-2 border-gray-700' : 'bg-slate-100 border-t-2 border-slate-300'}>
                <td colSpan={3} className={`px-4 py-3 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Grand total
                </td>
                <td className={`px-3 py-3 text-sm font-bold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                  {fmtDuration(totalHours)}
                </td>
                <td colSpan={3} className={`px-4 py-3 text-right text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {effectiveTeamMode ? 'Team · max duration per day' : `${entries.length} entries`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
